import {
    ArrowRight,
    CheckCircle2,
    Clock3,
    Loader2,
    // Mail,
    MapPin,
    Navigation,
    Phone,
} from "lucide-react";

import { Button } from "./components/Button/Button";
import Card from "./components/Card/Card";
import { ErrorState } from "./components/ErrorState/ErrorState";
import {
    useCurrentGarde,
    useNearbyOfficines,
    type PublicOfficine,
} from "./hooks/usePublicGarde";

function formatRange(startIso: string, endIso: string): string | null {
    const start = new Date(startIso);
    const end = new Date(endIso);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return null;
    }

    const fmt = new Intl.DateTimeFormat("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    });

    return `Du ${fmt.format(start)} au ${fmt.format(end)}`;
}

function formatDistance(meters: number): string {
    return meters < 1000
        ? `${Math.round(meters)} m`
        : `${(meters / 1000).toFixed(1)} km`;
}

function officineAddress(officine: PublicOfficine): string {
    return [officine.street, officine.district, officine.city]
        .filter(Boolean)
        .join(", ");
}

function directionsUrl(destination: string): string {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        destination,
    )}`;
}

function PharmacyCard({
    officine,
    compact = false,
}: {
    officine: PublicOfficine;
    compact?: boolean;
}) {
    const address = officineAddress(officine);

    return (
        <Card
            as="article"
            className={[
                "shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg",
                compact ? "p-4" : "p-5 sm:p-6",
            ].join(" ")}
        >
            <div className="flex items-start gap-3">
                <span
                    className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-primary"
                    aria-hidden="true"
                />

                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold text-foreground sm:text-lg">
                            {officine.name}
                        </h3>
                    </div>

                    <p className="mt-2 flex items-start gap-1.5 text-sm leading-relaxed text-muted-foreground">
                        <MapPin
                            className="mt-0.5 h-4 w-4 shrink-0"
                            aria-hidden="true"
                        />
                        <span>{address || "Tambacounda"}</span>
                    </p>
                </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-2">
                <a
                    href={`tel:${officine.phone}`}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-md"
                >
                    <Phone className="h-4 w-4" aria-hidden="true" />
                    Appeler
                </a>

                <a
                    href={directionsUrl(
                        `${officine.name}, ${address}, Tambacounda`,
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-secondary px-4 py-2.5 text-sm font-semibold text-secondary-foreground transition-colors hover:bg-accent"
                >
                    <MapPin className="h-4 w-4" aria-hidden="true" />
                    Itinéraire
                </a>
            </div>
        </Card>
    );
}

export default function Home() {
    const {
        data: garde,
        error: gardeError,
        isPending: isGardeLoading,
        isError: isGardeError,
        refetch: refetchGarde,
    } = useCurrentGarde();

    const {
        locate,
        geoState,
        officines,
        isLoading: isLocating,
    } = useNearbyOfficines();

    const dateRange = garde
        ? formatRange(garde.periodStart, garde.periodEnd)
        : null;

    const currentOfficines = garde?.officines ?? [];
    const firstOfficine = currentOfficines[0];

    return (
        <main>
            {/* Hero */}
            <section className="relative isolate overflow-hidden border-b border-border/70 bg-linear-to-br from-primary/10 via-background to-background">
                <div className="relative mx-auto grid max-w-7xl gap-10 px-5 py-12 sm:px-8 sm:py-16 lg:grid-cols-[1.08fr_0.92fr] lg:items-center lg:gap-14 lg:py-20">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-card/80 px-3.5 py-2 text-xs font-semibold text-primary shadow-sm backdrop-blur">
                            <span
                                className="h-2 w-2 rounded-full bg-primary shadow-[0_0_0_4px_rgba(4,135,137,0.12)]"
                                aria-hidden="true"
                            />
                            Pharmacies de garde à Tambacounda
                        </div>

                        <h1 className="mt-6 max-w-3xl text-4xl font-extrabold leading-[1.08] tracking-[-0.04em] text-foreground sm:text-5xl lg:text-6xl">
                            Trouvez une pharmacie de garde
                            <span className="block text-primary">
                                près de chez vous.
                            </span>
                        </h1>

                        <p className="mt-6 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
                            Consultez la garde actuelle, trouvez les pharmacies
                            proches de vous et appelez-les directement en
                            quelques secondes.
                        </p>

                        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                            <Button
                                onClick={locate}
                                isLoading={isLocating}
                                leftIcon={<Navigation className="h-5 w-5" />}
                                className="min-h-12 px-6 text-base shadow-lg shadow-primary/20 transition-transform hover:-translate-y-0.5"
                            >
                                Trouver autour de moi
                            </Button>

                            <a
                                href="#gardes"
                                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-border bg-card/80 px-6 text-sm font-semibold shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:bg-card"
                            >
                                Voir la garde actuelle
                                <ArrowRight
                                    className="h-4 w-4"
                                    aria-hidden="true"
                                />
                            </a>
                        </div>

                        <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
                            <span className="inline-flex items-center gap-1.5">
                                <CheckCircle2 className="h-4 w-4 text-primary" />
                                Garde actuelle
                            </span>

                            <span className="inline-flex items-center gap-1.5">
                                <CheckCircle2 className="h-4 w-4 text-primary" />
                                Géolocalisation
                            </span>

                            <span className="inline-flex items-center gap-1.5">
                                <CheckCircle2 className="h-4 w-4 text-primary" />
                                Appel direct
                            </span>
                        </div>
                    </div>

                    {/* Garde actuelle - Hero */}
                    <div className="rounded-3xl border border-border/70 bg-card p-5 shadow-[0_24px_80px_-36px_rgba(4,120,115,0.32)] ring-1 ring-white/80 sm:p-7">
                        <div className="flex items-center justify-between gap-3">
                            <div className="inline-flex items-center gap-2 text-xs font-bold tracking-wide text-primary">
                                <span
                                    className="h-2.5 w-2.5 animate-pulse rounded-full bg-primary"
                                    aria-hidden="true"
                                />
                                GARDE EN COURS
                            </div>
                        </div>

                        <h2 className="mt-5 text-xl font-bold tracking-tight sm:text-2xl">
                            Pharmacies actuellement de garde
                        </h2>

                        {isGardeLoading && (
                            <div className="mt-5 space-y-3">
                                <div className="h-5 w-48 animate-pulse rounded bg-muted" />
                                <div className="h-20 animate-pulse rounded-xl bg-muted" />
                            </div>
                        )}

                        {isGardeError && (
                            <div className="mt-5">
                                <ErrorState
                                    error={gardeError}
                                    fallbackMessage="Impossible de charger les informations de garde pour le moment."
                                    compact
                                    onRetry={() => {
                                        void refetchGarde();
                                    }}
                                />
                            </div>
                        )}

                        {!isGardeLoading && !isGardeError && !garde && (
                            <p className="mt-5 rounded-xl bg-muted p-4 text-sm leading-relaxed text-muted-foreground">
                                Aucune information de garde disponible pour le
                                moment.
                            </p>
                        )}

                        {!isGardeLoading && !isGardeError && garde && (
                            <>
                                {dateRange && (
                                    <p className="mt-1 text-sm text-muted-foreground">
                                        {dateRange}
                                    </p>
                                )}

                                {firstOfficine ? (
                                    <div className="mt-5">
                                        <PharmacyCard
                                            officine={firstOfficine}
                                        />

                                        {currentOfficines.length > 1 && (
                                            <a
                                                href="#gardes"
                                                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
                                            >
                                                Voir les{" "}
                                                {currentOfficines.length}{" "}
                                                pharmacies de garde
                                                <ArrowRight className="h-4 w-4" />
                                            </a>
                                        )}
                                    </div>
                                ) : (
                                    <p className="mt-5 rounded-xl bg-muted p-4 text-sm text-muted-foreground">
                                        Aucune officine n'est actuellement
                                        associée à cette garde.
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </section>

            {/* Comment ça marche */}
            <section
                aria-labelledby="how-it-works-title"
                className="border-b border-border/70 bg-card/50"
            >
                <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
                    <div className="max-w-2xl">
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">
                            Simple et rapide
                        </p>

                        <h2
                            id="how-it-works-title"
                            className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl"
                        >
                            Trouvez votre pharmacie en 3 étapes
                        </h2>
                    </div>

                    <div className="mt-9 grid gap-4 md:grid-cols-3">
                        {[
                            {
                                number: "01",
                                title: "Localisez-vous",
                                text: "Autorisez la géolocalisation pour rechercher les pharmacies disponibles autour de vous.",
                            },
                            {
                                number: "02",
                                title: "Consultez la garde",
                                text: "Visualisez les officines actuellement concernées par la garde à Tambacounda.",
                            },
                            {
                                number: "03",
                                title: "Appelez ou naviguez",
                                text: "Contactez directement la pharmacie ou obtenez votre itinéraire.",
                            },
                        ].map((step) => (
                            <div
                                key={step.number}
                                className="group rounded-2xl border border-border/80 bg-background p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg"
                            >
                                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-sm font-bold text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                                    {step.number}
                                </span>

                                <h3 className="mt-5 text-base font-bold">
                                    {step.title}
                                </h3>

                                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                                    {step.text}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Garde actuelle */}
            <section
                id="gardes"
                aria-labelledby="garde-title"
                className="scroll-mt-20"
            >
                <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">
                                Aujourd'hui
                            </p>

                            <h2
                                id="garde-title"
                                className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl"
                            >
                                Pharmacies de garde
                            </h2>

                            {dateRange && (
                                <p className="mt-1 text-sm text-muted-foreground">
                                    {dateRange}
                                </p>
                            )}
                        </div>
                    </div>

                    {isGardeLoading && (
                        <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                            <div className="h-48 animate-pulse rounded-2xl bg-muted" />
                            <div className="h-48 animate-pulse rounded-2xl bg-muted" />
                            <div className="h-48 animate-pulse rounded-2xl bg-muted" />
                        </div>
                    )}

                    {!isGardeLoading && !isGardeError && garde ? (
                        <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                            {garde.officines.map((officine) => (
                                <PharmacyCard
                                    key={officine._id}
                                    officine={officine}
                                />
                            ))}

                            {garde.peripheryReinforcement && (
                                <PharmacyCard
                                    officine={garde.peripheryReinforcement}
                                />
                            )}
                        </div>
                    ) : null}

                    {isGardeError && (
                        <p
                            role="alert"
                            className="mt-6 rounded-xl bg-muted p-4 text-sm text-muted-foreground"
                        >
                            Impossible de charger les informations de garde pour
                            le moment.
                        </p>
                    )}

                    {!isGardeLoading &&
                        !isGardeError &&
                        garde &&
                        garde.officines.length === 0 && (
                            <p className="mt-6 rounded-xl bg-muted p-4 text-sm text-muted-foreground">
                                Aucune officine n'est actuellement associée à
                                cette garde.
                            </p>
                        )}
                </div>
            </section>

            {/* Pharmacies proches */}
            <section
                id="proximite"
                aria-labelledby="nearby-title"
                className="border-y border-border/70 bg-muted/50"
            >
                <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
                    <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                        <div className="max-w-2xl">
                            <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">
                                Autour de vous
                            </p>

                            <h2
                                id="nearby-title"
                                className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl"
                            >
                                Trouver une pharmacie proche
                            </h2>

                            <p className="mt-2 text-sm leading-6 text-muted-foreground sm:text-base">
                                Utilisez votre position pour afficher les
                                pharmacies actives dans un rayon de 15 km.
                            </p>
                        </div>

                        {geoState.status !== "success" && (
                            <Button
                                onClick={locate}
                                isLoading={isLocating}
                                leftIcon={<Navigation className="h-4 w-4" />}
                                className="min-h-11 shrink-0"
                            >
                                Me localiser
                            </Button>
                        )}
                    </div>

                    {geoState.status === "error" && (
                        <p
                            role="alert"
                            className="mt-5 rounded-xl border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive"
                        >
                            {geoState.message}
                        </p>
                    )}

                    {geoState.status === "success" && (
                        <div className="mt-7">
                            {isLocating && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Recherche des pharmacies autour de vous...
                                </div>
                            )}

                            {!isLocating && officines.length === 0 && (
                                <div className="rounded-2xl border border-border bg-card p-6 text-center">
                                    <MapPin className="mx-auto h-8 w-8 text-muted-foreground" />

                                    <p className="mt-3 text-sm font-semibold">
                                        Aucune pharmacie active trouvée
                                    </p>

                                    <p className="mt-1 text-sm text-muted-foreground">
                                        Aucune pharmacie n'a été trouvée dans un
                                        rayon de 15 km.
                                    </p>
                                </div>
                            )}

                            {!isLocating && officines.length > 0 && (
                                <div className="overflow-hidden rounded-3xl border border-border/80 bg-card shadow-sm">
                                    <ul className="divide-y divide-border">
                                        {officines.map((officine) => (
                                            <li
                                                key={officine._id}
                                                className="flex items-center justify-between gap-4 p-4 transition-colors hover:bg-accent/50 sm:p-5"
                                            >
                                                <div className="flex min-w-0 items-start gap-3">
                                                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />

                                                    <div className="min-w-0">
                                                        <p className="truncate text-sm font-semibold text-foreground sm:text-base">
                                                            {officine.name}
                                                        </p>

                                                        <p className="mt-1 truncate text-xs text-muted-foreground sm:text-sm">
                                                            {formatDistance(
                                                                officine.distanceMeters,
                                                            )}{" "}
                                                            ·{" "}
                                                            {[
                                                                officine.street,
                                                                officine.district,
                                                                officine.city,
                                                            ]
                                                                .filter(Boolean)
                                                                .join(", ")}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex shrink-0 gap-2">
                                                    <a
                                                        href={`tel:${officine.phone}`}
                                                        title={`Appeler ${officine.name}`}
                                                        aria-label={`Appeler ${officine.name}`}
                                                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors hover:bg-primary/20"
                                                    >
                                                        <Phone className="h-4 w-4" />
                                                    </a>

                                                    <a
                                                        href={directionsUrl(
                                                            `${officine.coordinates[1]},${officine.coordinates[0]}`,
                                                        )}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title={`Itinéraire vers ${officine.name}`}
                                                        aria-label={`Itinéraire vers ${officine.name}`}
                                                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary text-secondary-foreground transition-colors hover:bg-accent"
                                                    >
                                                        <MapPin className="h-4 w-4" />
                                                    </a>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    {geoState.status === "idle" && (
                        <div className="mt-7 rounded-3xl border border-border/80 bg-card p-8 text-center shadow-sm">
                            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                                <Navigation className="h-5 w-5 text-primary" />
                            </div>

                            <h3 className="mt-4 text-base font-bold">
                                Où se trouve la pharmacie la plus proche ?
                            </h3>

                            <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-muted-foreground">
                                Autorisez votre position pour afficher les
                                pharmacies disponibles autour de vous.
                            </p>

                            <Button
                                onClick={locate}
                                isLoading={isLocating}
                                leftIcon={<Navigation className="h-4 w-4" />}
                                className="mt-5"
                            >
                                Me localiser
                            </Button>
                        </div>
                    )}
                </div>
            </section>

            {/* Pourquoi Pharma'Gard */}
            <section
                aria-labelledby="benefits-title"
                className="border-t border-border/70 bg-card/50"
            >
                <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
                    <div className="max-w-2xl">
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">
                            Pharmagard
                        </p>

                        <h2
                            id="benefits-title"
                            className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl"
                        >
                            Trouver une pharmacie sans perdre de temps
                        </h2>
                    </div>

                    <div className="mt-9 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {[
                            {
                                icon: Clock3,
                                title: "Rapide",
                                text: "Accédez rapidement aux informations de la garde actuelle.",
                            },
                            {
                                icon: MapPin,
                                title: "Local",
                                text: "Trouvez les pharmacies disponibles autour de votre position.",
                            },
                            {
                                icon: Phone,
                                title: "Direct",
                                text: "Appelez directement l'officine depuis votre téléphone.",
                            },
                            {
                                icon: CheckCircle2,
                                title: "Simple",
                                text: "Un parcours clair, pensé pour trouver rapidement une officine.",
                            },
                        ].map((benefit) => {
                            const Icon = benefit.icon;

                            return (
                                <Card
                                    as="article"
                                    key={benefit.title}
                                    className="bg-background p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg"
                                >
                                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10">
                                        <Icon className="h-5 w-5 text-primary" />
                                    </div>

                                    <h3 className="mt-5 font-bold">
                                        {benefit.title}
                                    </h3>

                                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                                        {benefit.text}
                                    </p>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section
                aria-labelledby="faq-title"
                className="border-t border-border/70"
            >
                <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
                    <div className="text-center">
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">
                            Besoin d'aide ?
                        </p>

                        <h2
                            id="faq-title"
                            className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl"
                        >
                            Questions fréquentes
                        </h2>
                    </div>

                    <div className="mt-9 divide-y divide-border overflow-hidden rounded-3xl border border-border/80 bg-card shadow-sm">
                        {[
                            {
                                question:
                                    "Qu'est-ce qu'une pharmacie de garde ?",
                                answer: "Une pharmacie de garde assure la continuité de l'accès aux médicaments en dehors des horaires habituels.",
                            },
                            {
                                question:
                                    "Comment trouver une pharmacie près de moi ?",
                                answer: "Cliquez sur « Trouver autour de moi » et autorisez la géolocalisation. Les pharmacies disponibles dans un rayon de 15 km seront affichées.",
                            },
                            {
                                question:
                                    "Puis-je appeler directement une pharmacie ?",
                                answer: "Oui. Chaque pharmacie affichée dispose d'un bouton permettant de l'appeler directement depuis votre téléphone.",
                            },
                            {
                                question:
                                    "Comment obtenir l'itinéraire vers une pharmacie ?",
                                answer: "Le bouton « Itinéraire » ouvre Google Maps avec la destination de l'officine.",
                            },
                        ].map((item) => (
                            <details
                                key={item.question}
                                className="group p-5 transition-colors open:bg-muted/50 sm:p-6"
                            >
                                <summary className="cursor-pointer list-none pr-8 text-sm font-semibold marker:hidden sm:text-base">
                                    <span className="relative block">
                                        {item.question}

                                        <span className="absolute right-0 top-0 text-xl font-normal text-muted-foreground transition-transform group-open:rotate-45">
                                            +
                                        </span>
                                    </span>
                                </summary>

                                <p className="mt-3 max-w-3xl text-sm leading-6 text-muted-foreground">
                                    {item.answer}
                                </p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pharmacien */}
            {/* <section
                aria-labelledby="pharmacien-title"
                className="border-t border-border/70 bg-muted/50"
            >
                <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
                    <div className="overflow-hidden rounded-3xl border border-primary/15 bg-card shadow-[0_24px_80px_-36px_rgba(4,120,115,0.3)]">
                        <div className="flex flex-col gap-8 bg-linear-to-br from-primary/10 via-card to-secondary/60 p-6 sm:p-8 lg:flex-row lg:items-center lg:justify-between lg:p-10">
                            <div className="max-w-2xl">
                                <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-card/80 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-primary">
                                    <span className="h-2 w-2 rounded-full bg-primary" />
                                    Rejoignez-nous
                                </div>

                                <h2
                                    id="pharmacien-title"
                                    className="mt-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl"
                                >
                                    Vous êtes pharmacien ?
                                </h2>

                                <p className="mt-4 text-base leading-7 text-muted-foreground">
                                    Rejoignez le réseau Pharmagard pour
                                    accroître votre visibilité, renforcer votre
                                    présence locale et offrir des conseils
                                    utiles à vos patients.
                                </p>

                                <p className="mt-3 text-sm leading-6 text-muted-foreground">
                                    Partagez des articles médicaux, des conseils
                                    pratiques et des ressources pour accompagner
                                    les patients au quotidien.
                                </p>
                            </div>

                            <div className="shrink-0">
                                <a
                                    href="https://app.pharmagard.sn/contact"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-xl"
                                >
                                    <Mail className="h-4 w-4" />
                                    Demander une adhésion
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section> */}
        </main>
    );
}
